'use client';

// FocusScope is not yet exposed by the unified `radix-ui` package used
// elsewhere in the monorepo (see famfam/components/ui/sheet.tsx — `import
// { Dialog as SheetPrimitive } from 'radix-ui'`). When `radix-ui` adds
// FocusScope, consolidate to the unified import to avoid a second Radix
// vendoring path.
import { c as _c } from "react/compiler-runtime";
import { FocusScope } from '@radix-ui/react-focus-scope';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn';
import { safeAreaPadding } from './safe-area';
import { useSwipeToDismiss } from './use-swipe-to-dismiss';

/** Shared props for all overlay role variants, controlling visibility, accessibility, and behavioral features. */
interface CommonOverlayProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;

  /** Portal into `document.body`. Defaults to `false`. */
  readonly portal?: boolean;
  /** Wrap children in a Radix trapped `FocusScope`. Defaults to `true`. */
  readonly trapFocus?: boolean;
  /** Set `document.body` `overflow: hidden` while open. Defaults to `true`. */
  readonly lockBodyScroll?: boolean;
  /** Install a document-level Escape handler. Defaults to `true`. */
  readonly closeOnEscape?: boolean;
  /**
   * Track the iOS soft-keyboard via `visualViewport.resize` and expose its
   * pixel height as the `--keyboard-height` CSS variable on the root. When
   * enabled, `padding-bottom` becomes
   * `max(env(safe-area-inset-bottom), var(--keyboard-height, 0px))`.
   * Defaults to `false`.
   */
  readonly trackKeyboard?: boolean;
  /**
   * Render a small grabber at the top using `useSwipeToDismiss`; that shared hook owns the
   * downward-drag threshold and deferred `onClose` transition. Defaults to `false`.
   */
  readonly swipeToClose?: boolean;
}

/** `role='dialog'` variant — accepts `ariaModal`. */
interface DialogOverlayProps extends CommonOverlayProps {
  readonly role?: 'dialog';
  /** Only meaningful for `role='dialog'`. Defaults to `true`. */
  readonly ariaModal?: boolean;
}

/** `role='complementary'` variant — `ariaModal` is not meaningful. */
interface ComplementaryOverlayProps extends CommonOverlayProps {
  readonly role: 'complementary';
  readonly ariaModal?: never;
}

/**
 * Discriminated union on `role` so TypeScript rejects
 * `<MobileFullScreenOverlay role="complementary" ariaModal />` at compile time.
 */
export type MobileFullScreenOverlayProps = DialogOverlayProps | ComplementaryOverlayProps;

/**
 * Full-viewport modal overlay for mobile UIs, with optional focus trapping,
 * body-scroll locking, swipe-to-dismiss, and keyboard-height tracking.
 */
export function MobileFullScreenOverlay(t0) {
  const $ = _c(40);
  const {
    open,
    onClose,
    children,
    role: t1,
    ariaLabel,
    ariaModal,
    className,
    style,
    portal: t2,
    trapFocus: t3,
    lockBodyScroll: t4,
    closeOnEscape: t5,
    trackKeyboard: t6,
    swipeToClose: t7
  } = t0;
  const role = t1 === undefined ? "dialog" : t1;
  const portal = t2 === undefined ? false : t2;
  const trapFocus = t3 === undefined ? true : t3;
  const lockBodyScroll = t4 === undefined ? true : t4;
  const closeOnEscape = t5 === undefined ? true : t5;
  const trackKeyboard = t6 === undefined ? false : t6;
  const swipeToClose = t7 === undefined ? false : t7;
  const rootRef = React.useRef(null);
  let t8;
  let t9;
  if ($[0] !== lockBodyScroll || $[1] !== open) {
    t8 = () => {
      if (!open || !lockBodyScroll) {
        return;
      }
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
      };
    };
    t9 = [open, lockBodyScroll];
    $[0] = lockBodyScroll;
    $[1] = open;
    $[2] = t8;
    $[3] = t9;
  } else {
    t8 = $[2];
    t9 = $[3];
  }
  React.useEffect(t8, t9);
  let t10;
  let t11;
  if ($[4] !== closeOnEscape || $[5] !== onClose || $[6] !== open) {
    t10 = () => {
      if (!open || !closeOnEscape) {
        return;
      }
      const handleKeyDown = event => {
        if (event.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t11 = [open, closeOnEscape, onClose];
    $[4] = closeOnEscape;
    $[5] = onClose;
    $[6] = open;
    $[7] = t10;
    $[8] = t11;
  } else {
    t10 = $[7];
    t11 = $[8];
  }
  React.useEffect(t10, t11);
  let t12;
  let t13;
  if ($[9] !== open || $[10] !== trackKeyboard) {
    t12 = () => {
      if (!open || !trackKeyboard) {
        return;
      }
      const viewport = window.visualViewport;
      if (!viewport) {
        return;
      }
      const handleResize = () => {
        const keyboardHeight = Math.max(0, window.innerHeight - viewport.height);
        rootRef.current?.style.setProperty("--keyboard-height", `${keyboardHeight}px`);
      };
      handleResize();
      viewport.addEventListener("resize", handleResize);
      return () => viewport.removeEventListener("resize", handleResize);
    };
    t13 = [open, trackKeyboard];
    $[9] = open;
    $[10] = trackKeyboard;
    $[11] = t12;
    $[12] = t13;
  } else {
    t12 = $[11];
    t13 = $[12];
  }
  React.useEffect(t12, t13);
  let t14;
  if ($[13] !== onClose) {
    t14 = {
      targetRef: rootRef,
      onDismiss: onClose
    };
    $[13] = onClose;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const swipeHandlers = useSwipeToDismiss(t14);
  if (!open) {
    return null;
  }
  let t15;
  if ($[15] !== trackKeyboard) {
    t15 = safeAreaPadding({
      trackKeyboard
    });
    $[15] = trackKeyboard;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  let t16;
  if ($[17] !== style || $[18] !== t15) {
    t16 = {
      ...t15,
      ...style
    };
    $[17] = style;
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const resolvedStyle = t16;
  let t17;
  if ($[20] !== className) {
    t17 = cn("fixed inset-0 bg-background flex flex-col", className);
    $[20] = className;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const rootClassName = t17;
  let t18;
  if ($[22] !== swipeHandlers || $[23] !== swipeToClose) {
    t18 = swipeToClose && <div data-swipe-handle={true} aria-hidden="true" {...swipeHandlers} className="flex shrink-0 items-center justify-center py-2 cursor-grab active:cursor-grabbing touch-none select-none"><div className="h-1 w-10 rounded-full" style={{
        backgroundColor: "currentColor",
        opacity: 0.25
      }} /></div>;
    $[22] = swipeHandlers;
    $[23] = swipeToClose;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== children || $[26] !== t18) {
    t19 = <>{t18}{children}</>;
    $[25] = children;
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  const innerContent = t19;
  let t20;
  if ($[28] !== ariaLabel || $[29] !== ariaModal || $[30] !== innerContent || $[31] !== resolvedStyle || $[32] !== role || $[33] !== rootClassName) {
    t20 = role === "dialog" ? <div ref={rootRef} role="dialog" aria-modal={ariaModal ?? true} aria-label={ariaLabel} className={rootClassName} style={resolvedStyle}>{innerContent}</div> : <aside ref={rootRef} aria-label={ariaLabel} className={rootClassName} style={resolvedStyle}>{innerContent}</aside>;
    $[28] = ariaLabel;
    $[29] = ariaModal;
    $[30] = innerContent;
    $[31] = resolvedStyle;
    $[32] = role;
    $[33] = rootClassName;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  const overlay = t20;
  let t21;
  if ($[35] !== overlay || $[36] !== trapFocus) {
    t21 = trapFocus ? <FocusScope trapped={true} asChild={true}>{overlay}</FocusScope> : overlay;
    $[35] = overlay;
    $[36] = trapFocus;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  const wrapped = t21;
  if (!portal || typeof document === "undefined") {
    return wrapped;
  }
  let t22;
  if ($[38] !== wrapped) {
    t22 = createPortal(wrapped, document.body);
    $[38] = wrapped;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  return t22;
}